import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { AnimatedBackground } from '../components/AnimatedBackground.jsx';
import { OminiaLogo } from '../components/OminiaLogo.jsx';
import { GOLD_MONTHLY_BRL, PLAN_COPY } from '../config/pricing.js';
import { fetchPublicPlanCatalog } from '../billing/publicPlans.js';
import { useTheme } from '../theme/ThemeContext.jsx';

const PLANS = {
  essential_trial: 'essential_trial',
  gold: 'gold',
};

function PlanPick({ selected, title, eyebrow, children, accent, onSelect }) {
  const selectedRing =
    accent === 'gold'
      ? 'border-amber-500/75 bg-amber-50 shadow-[inset_0_0_0_1px_rgba(245,158,11,0.25)] dark:border-amber-400/55 dark:bg-amber-950/35 dark:shadow-[inset_0_0_0_1px_rgba(251,191,36,0.2)]'
      : 'border-emerald-600/65 bg-emerald-50 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.28)] dark:border-emerald-400/55 dark:bg-emerald-950/40 dark:shadow-[inset_0_0_0_1px_rgba(52,211,153,0.18)]';
  const idle =
    'border-zinc-200 bg-zinc-50/95 hover:border-zinc-300 dark:border-zinc-700/85 dark:bg-zinc-900/55 dark:hover:border-zinc-600';

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`relative rounded-2xl border-2 px-4 py-3.5 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/55 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-950 ${
        selected ? selectedRing : idle
      }`}
    >
      {selected ? (
        <span
          className={`absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white ${
            accent === 'gold' ? 'bg-amber-600 dark:bg-amber-500' : 'bg-emerald-600 dark:bg-emerald-500'
          }`}
          aria-hidden
        >
          ✓
        </span>
      ) : null}
      <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-400">
        {eyebrow}
      </span>
      <span className="mt-2 block font-display text-base font-bold leading-snug tracking-tight text-zinc-900 dark:text-zinc-50">
        {title}
      </span>
      <span className="mt-2 block text-sm font-medium leading-relaxed text-zinc-600 dark:text-zinc-300">{children}</span>
    </button>
  );
}

export function Register() {
  const { scheme, toggle } = useTheme();
  const [params] = useSearchParams();
  const initialPlan = params.get('plan') === 'gold' ? PLANS.gold : PLANS.essential_trial;
  const navigate = useNavigate();

  const [plan, setPlan] = useState(initialPlan);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [planCopy, setPlanCopy] = useState(() => ({
    trial: { ...PLAN_COPY.trial, bullets: [...PLAN_COPY.trial.bullets] },
    gold: { ...PLAN_COPY.gold, bullets: [...PLAN_COPY.gold.bullets] },
  }));
  const [goldMonthlyBrl, setGoldMonthlyBrl] = useState(GOLD_MONTHLY_BRL);

  useEffect(() => {
    let cancelled = false;
    fetchPublicPlanCatalog().then((cat) => {
      if (cancelled) return;
      setPlanCopy(cat.copy);
      setGoldMonthlyBrl(cat.goldMonthlyBrl);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const planLabel = useMemo(() => {
    if (plan === PLANS.gold) {
      return `Plano Ouro — ${goldMonthlyBrl}/mês. Ao criar a empresa no painel, o sistema gera Client ID e Secret para o ERP (guarde o segredo na hora, como o token da loja).`;
    }
    const after = planCopy.trial.afterPriceLabel ? ` · ${planCopy.trial.afterPriceLabel}` : '';
    return `${planCopy.trial.priceLabel}${after}. ${planCopy.trial.subtitle} A integração com o sistema da empresa entra apenas no Ouro.`;
  }, [plan, goldMonthlyBrl, planCopy.trial]);

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api('/auth/register', {
        skipAuth: true,
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), email, whatsapp, password, plan }),
      });
      navigate(`/login?novo=1&email=${encodeURIComponent(email)}`);
    } catch (err) {
      setError(err.message || 'Não foi possível cadastrar');
    } finally {
      setBusy(false);
    }
  }

  const fieldCls =
    'mt-2 w-full rounded-xl border px-4 py-3 text-[15px] transition placeholder:text-zinc-400 outline-none bg-white border-zinc-200 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:bg-night-surface dark:border-zinc-600 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/15';

  return (
    <div className="relative min-h-screen overflow-x-hidden px-4 py-10 sm:py-14">
      <AnimatedBackground />
      <div className="relative z-10 mx-auto w-full max-w-lg">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <Link to="/" className="inline-flex flex-col gap-2 sm:gap-3">
            <OminiaLogo size={64} />
            <span className="font-display text-lg font-bold sm:text-xl">
              <span className="text-gradient">Omin</span>
              <span className="text-gradient-ia">IA</span>
            </span>
          </Link>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={toggle}
              className="rounded-full border border-zinc-300/90 bg-white/70 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-700 backdrop-blur-sm transition hover:bg-white dark:border-zinc-600 dark:bg-zinc-900/80 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              {scheme === 'dark' ? 'Claro' : 'Escuro'}
            </button>
            <Link
              className="rounded-full border border-emerald-700/25 bg-emerald-500/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-800 transition hover:bg-emerald-500/20 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-200 dark:hover:bg-emerald-500/20"
              to="/login"
            >
              Já tenho conta
            </Link>
          </div>
        </div>

        <div
          className="rounded-[1.75rem] border border-zinc-200/90 bg-white/95 p-7 shadow-[0_24px_80px_rgba(15,23,42,0.12)] backdrop-blur-xl sm:p-9
          dark:border-zinc-700/70 dark:bg-zinc-950/95 dark:shadow-[0_28px_90px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.06)]"
        >
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">Criar conta</h1>
          <p className="mt-3 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">{planLabel}</p>

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <PlanPick
              selected={plan === PLANS.essential_trial}
              eyebrow="Teste"
              title={
                planCopy.trial.afterPriceLabel
                  ? `${planCopy.trial.priceLabel} · ${planCopy.trial.afterPriceLabel}`
                  : planCopy.trial.priceLabel
              }
              accent="emerald"
              onSelect={() => setPlan(PLANS.essential_trial)}
            >
              {planCopy.trial.subtitle}
            </PlanPick>
            <PlanPick
              selected={plan === PLANS.gold}
              eyebrow="Ouro"
              title={planCopy.gold.priceLabel}
              accent="gold"
              onSelect={() => setPlan(PLANS.gold)}
            >
              <span className="text-amber-900 dark:text-amber-100">{planCopy.gold.subtitle}</span>
            </PlanPick>
          </div>

          <form className="mt-10 space-y-5" onSubmit={onSubmit}>
            <label className="block text-xs font-bold uppercase tracking-wide text-zinc-700 dark:text-zinc-300">
              Nome completo
              <input
                required
                autoComplete="name"
                className={fieldCls}
                placeholder="Como aparece no painel"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="block text-xs font-bold uppercase tracking-wide text-zinc-700 dark:text-zinc-300">
              E-mail
              <input
                required
                type="email"
                autoComplete="email"
                className={fieldCls}
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="block text-xs font-bold uppercase tracking-wide text-zinc-700 dark:text-zinc-300">
              WhatsApp (com DDI)
              <input
                required
                autoComplete="tel"
                className={fieldCls}
                placeholder="Ex.: 5511999998888"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
              />
            </label>
            <label className="block text-xs font-bold uppercase tracking-wide text-zinc-700 dark:text-zinc-300">
              Senha · mínimo 8 caracteres
              <input
                required
                minLength={8}
                type="password"
                autoComplete="new-password"
                className={fieldCls}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>

            {error ? (
              <p
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800 dark:border-red-900/50 dark:bg-red-950/50 dark:text-red-200"
                role="alert"
              >
                {error}
              </p>
            ) : null}

            <button
              disabled={busy}
              type="submit"
              className="w-full rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-white shadow-lg shadow-emerald-900/25 transition enabled:hover:brightness-105 enabled:active:scale-[0.99] disabled:opacity-55 dark:shadow-emerald-950/40"
            >
              {busy ? 'A criar conta…' : 'Finalizar cadastro'}
            </button>

            <p className="text-center text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-500">
              Ao continuar, aceita os termos da plataforma e política de privacidade descritos no site.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
